// The film's camera: a UI plane in real perspective, a shallow depth of field (blurred copies of the scene masked
// by a soft focus band), crossfade/blur transitions between shots, vignette and grain. All deterministic.
import React, {useContext} from 'react';
import {AbsoluteFill, Sequence, useCurrentFrame} from 'remotion';
import {FPS, ease, lerp, sec} from './theme';
import {useT} from './data';

// The camera looks at plane point (px,py) and puts it on screen at (sx,sy); rotations pivot on that point, so the
// subject stays put while the plane tilts. z > 0 moves the plane toward the lens.
export type Cam = {px: number; py: number; rx: number; ry: number; z: number; rz?: number; sx?: number; sy?: number};
// Linear focus band: CSS gradient angle, centre and half-width along it, feather (fractions of the gradient line).
export type Band = {a: number; c: number; w: number; f: number};
// Radial focus for macro shots: centre, sharp radius and feather (fractions of the frame width).
export type Spot = {cx: number; cy: number; r: number; f: number};
type Focus = Band | Spot;

export const ShotCtx = React.createContext({dur: 1});

const full = (c: Cam) => ({rz: 0, sx: 960, sy: 540, ...c});
const mix = <T extends Record<string, number>>(a: T, b: T, k: number) =>
 Object.fromEntries(Object.keys(a).map(key => [key, lerp(a[key], b[key], k)])) as T;
const pc = (x: number) => `${(x * 100).toFixed(2)}%`;
const isBand = (f: Focus): f is Band => 'a' in f;
const bandMask = (b: Band) => `linear-gradient(${b.a}deg, transparent ${pc(b.c - b.w - b.f)}, #000 ${pc(b.c - b.w)}, #000 ${pc(b.c + b.w)}, transparent ${pc(b.c + b.w + b.f)})`;
const spotMask = (s: Spot) => `radial-gradient(circle at ${pc(s.cx)} ${pc(s.cy)}, #000 ${(s.r * 1920).toFixed(0)}px, transparent ${((s.r + s.f) * 1920).toFixed(0)}px)`;
// sharp mask and the wider, softer mask of the half-blurred layer
const masks = (f: Focus): [string, string] => isBand(f)
 ? [bandMask(f), bandMask({...f, w: f.w + f.f * 0.6, f: f.f * 1.6})]
 : [spotMask(f), spotMask({...f, r: f.r + f.f * 0.6, f: f.f * 1.6})];

// One camera intention per shot: smootherstep from cam[0] to cam[1] over a window a little longer than the shot,
// so the move is already under way during the crossfade and never comes to a dead stop on screen.
export const useShotK = () => {
 const t = useT();
 const {dur} = useContext(ShotCtx);
 return ease((t + 0.7) / (dur + 1.4));
};

export const Lens: React.FC<{W: number; H: number; cam: [Cam, Cam]; focus: [Focus, Focus]; blur?: number; persp?: number; children: React.ReactNode}> =
 ({W, H, cam, focus, blur = 9, persp = 1500, children}) => {
  const k = useShotK();
  const c = mix(full(cam[0]), full(cam[1]), k);
  const f = mix(focus[0] as Record<string, number>, focus[1] as Record<string, number>, k) as Focus;
  const [sharp, soft] = masks(f);
  const scene = <AbsoluteFill style={{perspective: persp, perspectiveOrigin: `${c.sx}px ${c.sy}px`}}>
   <div style={{position: 'absolute', left: c.sx, top: c.sy, width: 0, height: 0, transformStyle: 'preserve-3d',
    transform: `translateZ(${c.z.toFixed(2)}px) rotateX(${c.rx.toFixed(3)}deg) rotateY(${c.ry.toFixed(3)}deg) rotateZ(${c.rz.toFixed(3)}deg)`}}>
    <div style={{position: 'absolute', left: -c.px, top: -c.py, width: W, height: H}}>{children}</div>
   </div>
  </AbsoluteFill>;
  return <AbsoluteFill>
   <AbsoluteFill style={{filter: `blur(${blur}px)`}}>{scene}</AbsoluteFill>
   <AbsoluteFill style={{filter: `blur(${(blur * 0.35).toFixed(2)}px)`, maskImage: soft, WebkitMaskImage: soft}}>{scene}</AbsoluteFill>
   <AbsoluteFill style={{maskImage: sharp, WebkitMaskImage: sharp}}>{scene}</AbsoluteFill>
  </AbsoluteFill>;
 };

// A shot on the timeline: quick crossfade with a blur on the way in and out.
export const Shot: React.FC<{from: number; to: number; inn?: number; out?: number; children: React.ReactNode}> = ({from, to, inn = 0.5, out = 0.5, children}) => {
 const f0 = sec(from), n = sec(to) - f0;
 return <Sequence from={f0} durationInFrames={n}>
  <ShotCtx.Provider value={{dur: n / FPS}}><Fade dur={n / FPS} inn={inn} out={out}>{children}</Fade></ShotCtx.Provider>
 </Sequence>;
};
const Fade: React.FC<{dur: number; inn: number; out: number; children: React.ReactNode}> = ({dur, inn, out, children}) => {
 const t = useT();
 const kIn = inn > 0 ? ease(t / inn) : 1, kOut = out > 0 ? 1 - ease((t - (dur - out)) / out) : 1;
 const k = Math.min(kIn, kOut);
 if (k >= 1) return <AbsoluteFill>{children}</AbsoluteFill>;
 return <AbsoluteFill style={{opacity: k, filter: `blur(${((1 - k) * 16).toFixed(2)}px)`}}>{children}</AbsoluteFill>;
};

// Post: vignette and a very light film grain (feTurbulence reseeded each frame).
export const Post: React.FC = () => {
 const frame = useCurrentFrame();
 return <AbsoluteFill style={{pointerEvents: 'none'}}>
  <AbsoluteFill style={{background: 'radial-gradient(ellipse 75% 75% at 50% 48%, rgba(0,0,0,0) 55%, rgba(0,0,0,.55) 100%)'}}/>
  <svg width={1920} height={1080} style={{position: 'absolute', inset: 0, opacity: 0.07, mixBlendMode: 'overlay'}}>
   <filter id="grain"><feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves={2} seed={frame % 97} stitchTiles="stitch"/><feColorMatrix type="saturate" values="0"/></filter>
   <rect width="100%" height="100%" filter="url(#grain)"/>
  </svg>
 </AbsoluteFill>;
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
